// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AuthContextType,
  User,
  RegisterRequestData,
} from '../types';
import { authAPI } from '../api/auth';
import { dashboardAPI } from '../api/dashboard';
import axios from 'axios';

// Export the context itself so it can be imported directly
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Default redirect path storage key
const REDIRECT_PATH_KEY = 'auth_redirect';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [redirectPath, setRedirectPath] = useState<string | null>(() => {
    // Initialize from sessionStorage on mount
    return sessionStorage.getItem(REDIRECT_PATH_KEY);
  });

  const navigate = useNavigate();

  // Set axios default authorization header
  const setAuthHeader = useCallback((token: string | null) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  // Persist auth data to localStorage and set header
  const persistAuth = useCallback(
    (newToken: string, newUser: User) => {
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      setToken(newToken);
      setUser(newUser);
      setAuthHeader(newToken);
    },
    [setAuthHeader]
  );

  // Clear auth data
  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setAuthHeader(null);
  }, [setAuthHeader]);

  // Load user from storage on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Validate token by fetching profile
          const response = await authAPI.getProfile();
          if (response.data.success && response.data.data?.user) {
            setUser(response.data.data.user);
            setToken(storedToken);
            setAuthHeader(storedToken);
          } else {
            // Token invalid, clear storage
            clearAuth();
          }
        } catch (error) {
          console.error('Failed to load user:', error);
          clearAuth();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [clearAuth, setAuthHeader]);

  // Helper to redirect based on user role
  const redirectBasedOnRole = useCallback((userRole: string) => {
    const path =
      userRole === 'admin' ? '/admin' :
      userRole === 'support' ? '/support' :
      userRole === 'rider' ? '/dashboard/rider' :
      userRole === 'customer' ? '/dashboard/customer' :
      '/choose-role';
    navigate(path, { replace: true });
  }, [navigate]);

  // Login with email/password
  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      if (response.data.success && response.data.data) {
        const { token, user } = response.data.data;
        persistAuth(token, user);
        
        // Check if user has role, if not redirect to choose role
        if (!user.role) {
          navigate('/choose-role');
        } else {
          redirectBasedOnRole(user.role);
        }
        return;
      }
      throw new Error(response.data.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Register new user
  const register = async (data: RegisterRequestData) => {
    setLoading(true);
    try {
      const response = await authAPI.register(data);
      if (response.data.success && response.data.data) {
        const { token, user } = response.data.data;
        persistAuth(token, user);
        redirectBasedOnRole(user.role);
        return;
      }
      throw new Error(response.data.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Google login (token from frontend OAuth)
  // src/context/AuthContext.tsx - Update the googleLogin method
// Replace your existing googleLogin with this version

const googleLogin = async (
  token: string,
  email?: string,
  name?: string,
  picture?: string
) => {
  setLoading(true);
  try {
    console.log('🔐 Google login attempt:', { email, name });
    
    const response = await authAPI.googleToken({
      token,
      email: email || '',
      name: name || '',
      picture: picture || '',
    });
    
    console.log('📡 Google token response:', response.data);
    
    if (response.data.success && response.data.data) {
      const { token: authToken, user } = response.data.data;
      persistAuth(authToken, user);
      
      // Check if user has a role
      if (!user.role) {
        console.log('🆕 New Google user, redirecting to role selection');
        navigate('/choose-role', { 
          state: { 
            fromGoogle: true, 
            userEmail: user.email,
            userName: user.fullName 
          } 
        });
      } else {
        console.log('✅ Existing Google user, redirecting to dashboard');
        redirectBasedOnRole(user.role);
      }
      return;
    }
    throw new Error(response.data.message || 'Google login failed');
  } catch (error: any) {
    console.error('❌ Google login error:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  // Logout
  const logout = () => {
    clearAuth();
    // Clear any redirect path
    clearAuthRedirect();
    navigate('/');
  };

  // Update profile
  const updateProfile = async (data: Partial<User>) => {
    setLoading(true);
    try {
      const response = await dashboardAPI.updateProfile(data);
      if (response.data.success && response.data.data?.user) {
        const updatedUser = response.data.data.user;
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } finally {
      setLoading(false);
    }
  };

  // Update user role (after signup)
  const updateUserRole = async (role: 'customer' | 'rider') => {
    setLoading(true);
    try {
      const response = await authAPI.updateRole(role);
      if (response.data.success && response.data.data) {
        const { token: newToken, user } = response.data.data;
        persistAuth(newToken, user);
        redirectBasedOnRole(user.role);
      }
    } catch (error: any) {
      console.error('Role update error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Helper to get current user role
  const getUserRole = () => user?.role;

  // Check if user has a specific role
  const hasRole = (role: 'customer' | 'rider') => user?.role === role;

  // Redirect path management
  const setAuthRedirect = (path: string) => {
    sessionStorage.setItem(REDIRECT_PATH_KEY, path);
    setRedirectPath(path);
  };

  const getAuthRedirect = () => {
    return sessionStorage.getItem(REDIRECT_PATH_KEY);
  };

  const clearAuthRedirect = () => {
    sessionStorage.removeItem(REDIRECT_PATH_KEY);
    setRedirectPath(null);
  };

  // Require authentication before executing an action
  const requireAuth = (action: () => void, fallbackPath?: string) => {
    if (user) {
      action();
    } else {
      if (fallbackPath) {
        setAuthRedirect(fallbackPath);
      }
      navigate('/login');
    }
  };

  // Context value
  const value: AuthContextType = {
    user,
    token,
    isLoading: loading,
    isAuthenticated: !!user,
    login,
    register,
    googleLogin,
    logout,
    updateProfile,
    updateUserRole,
    getUserRole,
    hasRole,
    redirectPath,
    setAuthRedirect,
    getAuthRedirect,
    clearAuthRedirect,
    requireAuth,
    persistAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};